import { fetchSharedFees, updateSharedFee } from './shared-fees-actions';
import { fetchCommissions as fetchOutsideBrokerCommissions, updateCommission as updateOutsideCommission } from './outside-broker-commissions-action';
import { fetchRebates, updateRebate } from './rebates-actions';
import { fetchTaxAllocations, updateTaxAllocation } from './tax-allocations-actions';
import { fetchCommissions, updateCommission } from './commissions-actions';
import { fetchAdditionalCommissions, fetchAdditionalCommissionTypeOptions } from './additional-commissions-actions';
import { fetchConsiderations, fetchLeaseConsiderationTypeOptions } from './considerations-actions';
import { fetchInstallments, fetchInstallmentOptions, updateInstallment } from './voucher/installments-actions';
import { updateDeal, updateDealPipelineTimestamp } from './deal-actions';
import * as commissionsService from '../../services/voucher/commissions-service';
import {
  FETCH_OUTSIDE_BROKER_COMMISSIONS_SUCCESS,
  FETCH_REBATES_SUCCESS,
  FETCH_SHARED_FEES_SUCCESS,
  FETCH_TAX_ALLOCATIONS_SUCCESS,
  VOUCHER_FETCH_COMMISSIONS_SUCCESS,
} from './action-types';
import {
  selectCBRECommissionIsPercent,
  selectCommissions,
  selectFeeSharingIsPercentage,
  selectInstallmentIsPercentage,
  selectInstallments,
  selectOutsideBrokerCommissions,
  selectOutsideBrokerIsPercentage,
  selectRebateIsPercentage,
  selectRebates,
  selectSharedFees,
  selectTaxAllocations,
  selectTaxIsPercentage,
  selectTotalCommissions,
  selectTotalCommissionsForCommissions,
  selectTotalCommissionsForInstallments,
  selectTotalCommissionsForRebates,
  selectTotalCommissionsForSharedFees,
  selectTotalCommissionsForTaxAllocations,
} from '../selectors/voucher-selectors';
import calculatePercentValueFromTotal from '../../utils/calculate-percent-value-from-total';

// dispatch
const fetchFinancials = (dealId, dealType) => (dispatch) => {
  // Options/lookups: fire-and-forget (no await needed)
  dispatch(fetchAdditionalCommissionTypeOptions());
  dispatch(fetchInstallmentOptions());
  if (dealType === 'lease') {
    dispatch(fetchLeaseConsiderationTypeOptions());
  }
  // Data calls: return Promise.all so callers can await all data being ready
  return Promise.all([
    dispatch(fetchSharedFees({ transaction: dealId, payment_group: 4 })),
    dispatch(fetchOutsideBrokerCommissions({ transaction: dealId, payment_group: 2 })),
    dispatch(fetchRebates({ transaction: dealId, payment_group: 3 })),
    dispatch(fetchTaxAllocations({ transaction: dealId, payment_group: 5 })),
    dispatch(fetchCommissions({ transaction: dealId })),
    dispatch(fetchAdditionalCommissions(dealId)),
    dispatch(fetchConsiderations(dealId)),
    dispatch(fetchInstallments(dealId)),
  ]);
};

const fetchConsiderationSection = (dealId, dealType) => (dispatch) => {
  dispatch(fetchAdditionalCommissionTypeOptions());
  if (dealType === 'lease') {
    dispatch(fetchLeaseConsiderationTypeOptions());
  }
  return Promise.all([
    dispatch(fetchAdditionalCommissions(dealId)),
    dispatch(fetchConsiderations(dealId)),
  ]);
};

const fetchAllocationsSection = dealId => dispatch => Promise.all([
  dispatch(fetchOutsideBrokerCommissions({ transaction: dealId, payment_group: 2 })),
  dispatch(fetchSharedFees({ transaction: dealId, payment_group: 4 })),
  dispatch(fetchRebates({ transaction: dealId, payment_group: 3 })),
  dispatch(fetchTaxAllocations({ transaction: dealId, payment_group: 5 })),
  dispatch(fetchAdditionalCommissions(dealId)),
]);

const fetchCommissionsSection = dealId => dispatch => dispatch(fetchCommissions({ transaction: dealId }));

const fetchInstallmentsSection = dealId => (dispatch) => {
  dispatch(fetchInstallmentOptions());
  return dispatch(fetchInstallments(dealId));
};

const fetchBillToSection = dealId => dispatch => dispatch(fetchOutsideBrokerCommissions({ transaction: dealId, payment_group: 2 }));

const PAYMENT_GROUPS = {
  CBRE_BROKER: 1,
  OUTSIDE_BROKER: 2,
  REBATE: 3,
  FEE_SHARE: 4,
  TAX: 5,
};

const fetchAllFinancialBreakdownAllocations = dealId => dispatch => commissionsService.fetchCommissions({ transaction: dealId })
  .then((commissions) => {
    dispatch({
      type: FETCH_OUTSIDE_BROKER_COMMISSIONS_SUCCESS,
      payload: commissions.filter(commission => commission.payment_group === PAYMENT_GROUPS.OUTSIDE_BROKER),
    });
    dispatch({
      type: FETCH_SHARED_FEES_SUCCESS,
      payload: commissions.filter(commission => commission.payment_group === PAYMENT_GROUPS.FEE_SHARE),
    });
    dispatch({
      type: FETCH_REBATES_SUCCESS,
      payload: commissions.filter(commission => commission.payment_group === PAYMENT_GROUPS.REBATE),
    });
    dispatch({
      type: FETCH_TAX_ALLOCATIONS_SUCCESS,
      payload: commissions.filter(commission => commission.payment_group === PAYMENT_GROUPS.TAX),
    });
    dispatch({
      type: VOUCHER_FETCH_COMMISSIONS_SUCCESS,
      payload: commissions.filter(commission => commission.payment_group === PAYMENT_GROUPS.CBRE_BROKER),
    });
  })
  .catch(console.error);

const fetchFinancialBreakdownData = dealId => dispatch => Promise.all([
  dispatch(fetchConsiderations(dealId)),
  dispatch(fetchAdditionalCommissions(dealId)),
  dispatch(fetchAllFinancialBreakdownAllocations(dealId)),
  dispatch(fetchInstallments(dealId)),
]);

// patch a commission as needed using new base total to calculate
const patchItems = (dispatch, call, list, isPercent, baseTotal, keys = { value: 'gross_commission', percentage: 'voucher_gross_commission_percent' }) => {
  list.forEach((item) => {
    const changes = calculatePercentValueFromTotal(
      baseTotal, isPercent,
      isPercent
        ? item[keys.percentage]
        : item[keys.value],
      keys,
    );
    if (item[keys.percentage] !== changes[keys.percentage] || item[keys.value] !== changes[keys.value]) {
      dispatch(call(item.id, changes, true));
    }
  });
};

export const FINANCIAL_TYPES = {
  CONSIDERATION: 'CONSIDERATION',
  ADDITIONAL_COMMISSION: 'ADDITIONAL_COMMISSION',
  REBATE: 'REBATE',
  SHARED_FEE: 'SHARED_FEE',
  COMMISSION: 'COMMISSION',
  INSTALLMENT: 'INSTALLMENT',
};

const shouldSkipBaseTotalUpdate = (previousTotal, totalCommissions, type) => (
  totalCommissions === previousTotal
  && (type === FINANCIAL_TYPES.CONSIDERATION || type === FINANCIAL_TYPES.ADDITIONAL_COMMISSION)
);

const updateLoadedFinancials = (dealId, type, previousTotal, options = {}) => async (dispatch, getState) => {
  const totalCommissions = selectTotalCommissions(getState());
  if (!options.force && shouldSkipBaseTotalUpdate(previousTotal, totalCommissions, type)) {
    return;
  }

  if (!options.skipDealAndTimestampUpdate) {
    dispatch(updateDeal(dealId, { estimated_commission: totalCommissions, modified: new Date() }));
  }

  const totalCommissionsForRebates = selectTotalCommissionsForRebates(getState());
  const totalCommissionsForSharedFees = selectTotalCommissionsForSharedFees(getState());
  const totalCommissionsForCommissions = selectTotalCommissionsForCommissions(getState());
  const totalCommissionsForInstallments = selectTotalCommissionsForInstallments(getState());
  const totalCommissionsForTaxAllocations = selectTotalCommissionsForTaxAllocations(getState());

  const outsideBrokerCommissions = selectOutsideBrokerCommissions(getState());
  const outsideBrokerIsPercent = selectOutsideBrokerIsPercentage(getState());
  patchItems(dispatch, updateOutsideCommission, outsideBrokerCommissions, outsideBrokerIsPercent, totalCommissions);

  const sharedFees = selectSharedFees(getState());
  const feeSharingIsPercent = selectFeeSharingIsPercentage(getState());
  patchItems(dispatch, updateSharedFee, sharedFees, feeSharingIsPercent, totalCommissionsForSharedFees);

  const rebates = selectRebates(getState());
  const rebatesIsPercentage = selectRebateIsPercentage(getState());
  patchItems(dispatch, updateRebate, rebates, rebatesIsPercentage, totalCommissionsForRebates);

  const taxAllocations = selectTaxAllocations(getState());
  const taxIsPercentage = selectTaxIsPercentage(getState());
  patchItems(dispatch, updateTaxAllocation, taxAllocations, taxIsPercentage, totalCommissionsForTaxAllocations);

  const commissions = selectCommissions(getState());
  const commissionsIsPercent = selectCBRECommissionIsPercent(getState());
  patchItems(dispatch, updateCommission, commissions, commissionsIsPercent, totalCommissionsForCommissions);

  const installments = selectInstallments(getState());
  const installmentsIsPercentage = selectInstallmentIsPercentage(getState());
  patchItems(
    dispatch,
    updateInstallment,
    installments,
    installmentsIsPercentage,
    totalCommissionsForInstallments,
    { value: 'installment_amount', percentage: 'voucher_installment_percent' },
  );

  if (!options.skipDealAndTimestampUpdate) {
    dispatch(updateDealPipelineTimestamp(dealId));
  }
};

const updateFinancials = (dealId, type) => async (dispatch, getState) => {
  const state = getState();
  const previousTotal = selectTotalCommissions(state);
  // get considerations and additional commissions to calculate base total
  await dispatch(fetchConsiderations(dealId));
  await dispatch(fetchAdditionalCommissions(dealId));
  await dispatch(fetchOutsideBrokerCommissions({ transaction: dealId, payment_group: 2 }));

  const totalCommissions = selectTotalCommissions(getState());
  // TODO, when do we need to run updates? should we send a type so it just does some or?
  if (shouldSkipBaseTotalUpdate(previousTotal, totalCommissions, type)) {
    return;
  }
  // update deal base total
  dispatch(updateDeal(dealId, { estimated_commission: totalCommissions, modified: new Date() }));

  // fetch data
  await dispatch(fetchSharedFees({ transaction: dealId, payment_group: 4 }));
  await dispatch(fetchRebates({ transaction: dealId, payment_group: 3 }));
  await dispatch(fetchTaxAllocations({ transaction: dealId, payment_group: 5 }));
  await dispatch(fetchCommissions({ transaction: dealId }));
  await dispatch(fetchInstallments(dealId));

  // get totals
  const totalCommissionsForRebates = selectTotalCommissionsForRebates(getState());
  const totalCommissionsForSharedFees = selectTotalCommissionsForSharedFees(getState());
  const totalCommissionsForCommissions = selectTotalCommissionsForCommissions(getState());
  const totalCommissionsForInstallments = selectTotalCommissionsForInstallments(getState());
  const totalCommissionsForTaxAllocations = selectTotalCommissionsForTaxAllocations(getState());

  // update outside broker commissions
  const outsideBrokerCommissions = selectOutsideBrokerCommissions(getState());
  const outsideBrokerIsPercent = selectOutsideBrokerIsPercentage(getState());
  patchItems(dispatch, updateOutsideCommission, outsideBrokerCommissions, outsideBrokerIsPercent, totalCommissions);

  // update shared fees
  const sharedFees = selectSharedFees(getState());
  const feeSharingIsPercent = selectFeeSharingIsPercentage(getState());
  patchItems(dispatch, updateSharedFee, sharedFees, feeSharingIsPercent, totalCommissionsForSharedFees);

  // update rebates
  const rebates = selectRebates(getState());
  const rebatesIsPercentage = selectRebateIsPercentage(getState());
  patchItems(dispatch, updateRebate, rebates, rebatesIsPercentage, totalCommissionsForRebates);

  // update tax allocations
  const taxAllocations = selectTaxAllocations(getState());
  const taxIsPercentage = selectTaxIsPercentage(getState());
  patchItems(dispatch, updateTaxAllocation, taxAllocations, taxIsPercentage, totalCommissionsForTaxAllocations);

  // update commissions employee, office, pool
  const commissions = selectCommissions(getState());
  const commissionsIsPercent = selectCBRECommissionIsPercent(getState());
  patchItems(dispatch, updateCommission, commissions, commissionsIsPercent, totalCommissionsForCommissions);

  // update installments
  const installments = selectInstallments(getState());
  const installmentsIsPercentage = selectInstallmentIsPercentage(getState());
  patchItems(
    dispatch,
    updateInstallment,
    installments,
    installmentsIsPercentage,
    totalCommissionsForInstallments,
    { value: 'installment_amount', percentage: 'voucher_installment_percent' },
  );
  // update pipeline timestamp
  dispatch(updateDealPipelineTimestamp(dealId));
};

export {
  fetchAllocationsSection,
  fetchBillToSection,
  fetchCommissionsSection,
  fetchFinancialBreakdownData,
  fetchConsiderationSection,
  fetchFinancials,
  fetchInstallmentsSection,
  updateLoadedFinancials,
  updateFinancials,
};
